import { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import { Spin } from "antd";
import axios from "axios";
import * as Sentry from "@sentry/react";
import "./style.css";

// Global API timeout — safety net for truly hung requests only.
// Per-context timeouts are set in sync/payment flows (60s/120s).
// This catches anything that slips through without a specific timeout.
axios.defaults.timeout = 60000;

// UI Watchdog — detects frozen UI and auto-reloads after 15s
import { startWatchdog } from "../utils/watchdog";
// Health Reporter — collects metrics, computes health score, reports every 5min
import { startHealthReporter } from "../utils/healthReporter";
startWatchdog();
startHealthReporter();
import ErrorBoundary from "./ErrorBoundary";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { EventBusProvider } from "../lib/components/EventBusProvider";
import OrderNotification from "./OrderNotification";
import SignIn from "./Authentication";
import tenantConfig from "../config/tenantConfig";
import { initTenantStorage } from "../config/tenantStorage";
import useAuthStore from "../stores/useAuthStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useCartStore from "../stores/useCartStore";
import useSessionStore from "../stores/useSessionStore";
import useTransactionStore from "../stores/useTransactionStore";
import useRestaurantStore from "../stores/useRestaurantStore";
import useReferenceStore from "../stores/useReferenceStore";
import useAdvanceOrderStore from "../stores/useAdvanceOrderStore";
import PrivateRoute from "./PrivateRoute";
import NavigationBlocker from "../NavigationBlocker";

import TillModalHandler from "./TillModalHandler";
import SupportButton from "./Support/SupportButton";
import CrashRecoveryProvider from "./CrashRecoveryProvider";
import SingleInstanceGuard from "./SingleInstanceGuard";
import { installPrintInterceptor } from "../lib/printer/printInterceptor";
import { checkAppVersionGate, checkSchemaVersionGate, stampCurrentVersion } from "../utils/appVersionGate";
import { trace } from "../utils/debugTrace";
import { pickDefaultRoute, parseTillAccess } from "../constants/accessControl";

// POS routes
import PointOfSale from "./PointOfSale";
import DualScreenPOS from "./dualScreen";
import Till from "./Till/Till";
import Login from "./Login";
import Sync from "./Sync";
import OpenTill from "./OpenTill";
import CloseTill from "./CloseTill";
import CloseTillAll from "./CloseTill/CloseTIllAll";
import CashToKeep from "./CloseTill/cashToKeep";
import OpenTillComponent from "./OpenTill/OpenTillComponent";
import Dashboard2 from "./Dashboard2";
import ProductionOrder from "./ProductionOrder";
import ProductionRMIssue from "./ProductionRMIssue";
import PurchaseOrder from "./PurchaseOrder";
import Inventory from "./Inventory";
import Aggregator from "./Aggregator";
import CallCenter from "./CallCenter";
import Estimate from "./Estimate";
import Preorder from "./PreOrder";
import RestaurantPreorder from "./PointOfSale/Restaurant/restaurantPreorder";
import TableManagement from "./PointOfSale/Restaurant/Tables-Management/tables";
import HealthMonitor from "./HealthMonitor";
import { WebSocketProvider, useWebSocket } from "../lib/components/WebSocketProvider";
import { getSalesHistoryDetails } from "../utils/salesHistoryUtils";

// Auth-aware redirect: the till's own reachable screen if an active session exists,
// /till if authenticated but no session, /sign-in if not authenticated.
// TSK-2997: was hardcoded to /pos, which sent estimate/production-only tills to a
// screen they have no access to.
function AuthRedirect() {
  const hasAuth = document.cookie.includes("accessToken=");
  const tillSession = useSessionStore.getState().tillSession;
  const tillData = useAuthStore.getState().tillData;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const hasActiveSession = tillSession?.tillStatus === "open" && tillData?.tillAccess && posConfig;
  const redirectTo = hasAuth ? (hasActiveSession ? pickDefaultRoute({ posConfig, tillaccess: parseTillAccess(tillData) }) : "/till") : "/sign-in";
  return <Redirect to={redirectTo} />;
}

const App = () => {
  const SentryDSN = process.env.REACT_APP_SENTRY_DSN;
  const SentryEnabled = process.env.REACT_APP_ENABLE_SENTRY;
  const environment = process.env.REACT_APP_Sentry_Environment;
  const [tenantReady, setTenantReady] = useState(false);

  const setTokens = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchKey = searchParams.get("searchKey");
    const nameTill = searchParams.get("nameTill");
    if (searchKey) {
      localStorage.setItem("tiilId", searchKey);
      localStorage.setItem("tillName", nameTill);
    }
  };

  useEffect(() => {
    let userPreferences = JSON.parse(localStorage.getItem("userPreferences"));
    if (!userPreferences?.dateFormat) {
      userPreferences = {
        dateFormat: "DD-MM-YYYY",
        dateTimeFormat: "DD-MM-YYYY HH:mm:ss",
      };
      localStorage.setItem("userPreferences", JSON.stringify(userPreferences));
    }
    setTokens();

    // Install print interceptor for Electron embedded printing
    if (window.electronAPI) {
      installPrintInterceptor();
    }

    const initTenant = async () => {
      const bootStart = performance.now();
      trace("BootDiag", "initTenant start", { path: window.location.pathname });
      try {
        // Version gate: clear stale localStorage when app version or schema changes
        const versionChanged = checkAppVersionGate();
        const schemaChanged = !versionChanged && checkSchemaVersionGate();
        if (versionChanged || schemaChanged) {
          window.location.reload();
          return;
        }
        stampCurrentVersion();

        // Apply a cached tenant prefix synchronously, before any localStorage
        // reads happen below, instead of waiting on tenantConfig.initialize()'s
        // async window.electronAPI.tenant.getActive() IPC round-trip. tillData
        // (useAuthStore, raw localStorage) is only ever written under the
        // tenant-prefixed key (see tenantStorage.js), but until now the prefix
        // was applied ONLY after that IPC call resolved truthy for THIS load.
        // Any time it was momentarily unready/slow/null on a fast refresh,
        // rehydrateFromStorage() below read the unprefixed key instead and
        // found nothing — tillData came back null even though it was safely
        // on disk, forcing a bogus /till → Login → full processSync detour.
        // posConfig never had this problem: it persists via zustand + a
        // sqliteStorage adapter, not this localStorage prefix mechanism.
        const cachedTenantCode = localStorage.getItem("cw-active-tenant");
        if (cachedTenantCode) {
          initTenantStorage(cachedTenantCode);
        }

        const activeTenant = await tenantConfig.initialize();
        trace("BootDiag", "activeTenant resolved", { hasActiveTenant: !!activeTenant, code: activeTenant?.code || null, cachedTenantCode });
        if (activeTenant) {
          initTenantStorage(activeTenant.code);
        }

        try {
          await useAuthStore.getState().rehydrateFromStorage();
        } catch (e) {
          console.error("[App] Auth rehydrate failed:", e);
        }

        const tillDataAfterRehydrate = useAuthStore.getState().tillData;

        const results = await Promise.allSettled([
          usePOSConfigStore.persist.rehydrate(),
          useCartStore.persist.rehydrate(),
          useSessionStore.persist.rehydrate(),
          useTransactionStore.persist.rehydrate(),
          useRestaurantStore.persist.rehydrate(),
          useReferenceStore.persist.rehydrate(),
          useAdvanceOrderStore.persist.rehydrate(),
        ]);
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
          console.error(
            "[App] Store rehydration failures:",
            failed.map((r) => r.reason)
          );
        }

        // BUG-1564: Restore the POS route after browser refresh.
        // CW Browser resets the URL to / on F5, which hits AuthRedirect → /till.
        // After full rehydration, if the session is valid and we're on a reset
        // path, replace the URL before the router renders — no flicker.
        // TSK-2997: resolve the target from the till's own mode instead of a
        // hardcoded /pos, so an estimate/production till doesn't take an extra
        // /pos → gate → real screen hop on every refresh.
        const rehydratedSession = useSessionStore.getState().tillSession;
        const rehydratedTill = useAuthStore.getState().tillData;
        const rehydratedPosConfig = usePOSConfigStore.getState().posConfig;
        const hasAuth = document.cookie.includes("accessToken=");
        const path = window.location.pathname;
        trace("BootDiag", "rehydration complete", {
          elapsedMs: Math.round(performance.now() - bootStart),
          hasAuth,
          hasTillSession: !!rehydratedSession,
          tillStatus: rehydratedSession?.tillStatus,
          hasTillData: !!rehydratedTill?.tillAccess,
          hasPosConfig: !!rehydratedPosConfig,
          storeRehydrateFailures: failed.length,
        });
        if (
          hasAuth &&
          rehydratedSession?.tillStatus === "open" &&
          rehydratedTill?.tillAccess &&
          rehydratedPosConfig &&
          (path === "/" || path === "/till")
        ) {
          const restoreTo = pickDefaultRoute({
            posConfig: rehydratedPosConfig,
            tillaccess: parseTillAccess(rehydratedTill),
          });
          window.history.replaceState(null, "", restoreTo);
        }

        trace("BootDiag", "tenantReady=true", { elapsedMs: Math.round(performance.now() - bootStart) });
        setTenantReady(true);
      } catch (err) {
        console.error("[App] Tenant init failed:", err);
        trace("BootDiag", "tenantReady=true (after error)", { elapsedMs: Math.round(performance.now() - bootStart), error: err?.message });
        setTenantReady(true);
      }
    };
    initTenant();
  }, []);

  useEffect(() => {
    if (SentryEnabled) {
      Sentry.init({
        environment: environment,
        dsn: SentryDSN,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: 1.0,
        ignoreErrors: [
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          "ResizeObserver loop completed with undelivered notifications.",
        ],
      });
    }
  }, []);

  const wsUrl = process.env.REACT_APP_WS_URL;

  // Wait for tenant to load before rendering the app.
  // Uses the same antd <Spin size="large" /> as the index.html boot splash so the
  // loading visual stays consistent from hard-reload through tenant/store rehydration.
  if (!tenantReady) {
    return (
      <div className="aspectRatio" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <SingleInstanceGuard>
      <AppShell wsUrl={wsUrl} />
    </SingleInstanceGuard>
  );
};

const AppShell = ({ wsUrl }) => {
  const tillData = useAuthStore((s) => s.tillData);
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId || null;
  const tillId = tillData?.tillAccess?.cwrTill?.till || null;

  return (
    <div className="aspectRatio" style={{ display: "flex", flexDirection: "column" }}>
      <CrashRecoveryProvider getSessionState={() => ({})} onRestore={() => {}}>
        <I18nextProvider i18n={i18n}>
          <EventBusProvider>
            <WebSocketProvider url={wsUrl} bunitId={bunitId} tillId={tillId} enabled={!!bunitId} debug={true}>
              <AppContent />
            </WebSocketProvider>
          </EventBusProvider>
        </I18nextProvider>
      </CrashRecoveryProvider>
      <SupportButton />
    </div>
  );
};

const AppContent = () => {
  const { visibleOrders, lastMessage, removeVisibleOrder, removeVisibleOrderByExternalId } = useWebSocket();

  const handleAutoAccept = async (orderData, { isManualAccept } = {}) => {
    console.debug("[ORDER-ACCEPT-DIAG] handleAutoAccept called", { orderId: orderData?.data?.orderId, isManualAccept });
    if (isManualAccept && orderData?.data?.orderId) {
      const result = await getSalesHistoryDetails(orderData?.data?.orderId, "AK", true);
      console.debug("[ORDER-ACCEPT-DIAG] getSalesHistoryDetails (auto-print) returned", { orderId: orderData?.data?.orderId, hadResult: !!result });
    } else {
      console.debug("[ORDER-ACCEPT-DIAG] handleAutoAccept skipped print call", { isManualAccept, hasOrderId: !!orderData?.data?.orderId });
    }
    removeVisibleOrder(orderData?.data?.orderId);
  };

  const handleOrderUpdate = async (data) => {
    try {
      console.debug("[ORDER-ACCEPT-DIAG] handleOrderUpdate received order:update", { new_state: data?.new_state, order_id: data?.order_id, external_channel_order_id: data?.additional_info?.external_channel?.order_id });
      if (data?.new_state === "Acknowledged") {
        const externalOrderId = data?.additional_info?.external_channel?.order_id;
        console.debug("Handling order:update for external order ID:", externalOrderId);
        const orderDetails = await getSalesHistoryDetails(externalOrderId, "AK", true, data?.order_id);

        if (!orderDetails) {
          console.error(`No order details for external order ID: ${externalOrderId}`);

          return;
        }

        removeVisibleOrderByExternalId(externalOrderId);
      } else {
        console.debug("[ORDER-ACCEPT-DIAG] order:update ignored — new_state is not 'Acknowledged'", { new_state: data?.new_state });
      }
    } catch (error) {
      console.error("Error handling websocket order:update:", error);
    }
  };

  useEffect(() => {
    if (!lastMessage) return;

    const eventType = lastMessage?.type || lastMessage?.event;

    if (eventType === "order:update") {
      handleOrderUpdate(lastMessage?.data);
    }
  }, [lastMessage]);

  const handleReject = (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  const handleOnClose = (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  return (
    <Router>
      <ErrorBoundary>
        <NavigationBlocker />
        <TillModalHandler />
        {visibleOrders.map(
          (order, index) => (
            // window.location.pathname !== "/aggregator" ? (
            <OrderNotification
              key={order.data.orderId}
              orderData={order}
              index={index}
              onAutoAccept={(options) => handleAutoAccept(order, options)}
              onReject={() => handleReject(order)}
              onClose={() => handleOnClose(order)}
            />
          )
          // ) : null
        )}
        <Switch>
          {/* Public routes */}
          <Route exact path="/sign-in" component={SignIn} />
          <Route exact path="/health" component={HealthMonitor} />
          <Route exact path="/till" component={Till} />
          <Route exact path="/login" component={Login} />

          {/* Private POS routes */}
          <PrivateRoute exact path="/pos" component={PointOfSale} />
          <PrivateRoute exact path="/dualscreen" component={DualScreenPOS} />
          <PrivateRoute exact path="/sync" component={Sync} />
          <PrivateRoute exact path="/dashboard2" component={Dashboard2} errorBoundary="Dashboard" />
          <PrivateRoute exact path="/production" component={ProductionOrder} errorBoundary="Production" />
          <PrivateRoute exact path="/production-rm-issue" component={ProductionRMIssue} errorBoundary="Production RM" />
          <PrivateRoute exact path="/purchaseOrder" component={PurchaseOrder} errorBoundary="Purchase Order" />
          <PrivateRoute exact path="/inventory/stock-indent" component={Inventory} errorBoundary="Inventory" />
          <PrivateRoute exact path="/aggregator" component={Aggregator} errorBoundary="Aggregator" />
          <PrivateRoute exact path="/estimate" component={Estimate} errorBoundary="Estimate" />
          <PrivateRoute exact path="/preorder" component={Preorder} errorBoundary="Preorder" />
          <PrivateRoute exact path="/restaurant/preorder" component={RestaurantPreorder} errorBoundary="Restaurant Preorder" />
          <PrivateRoute exact path="/open-till" component={OpenTill} />
          <PrivateRoute exact path="/cash-to-keep" component={CashToKeep} />
          <PrivateRoute exact path="/open-till-component" component={OpenTillComponent} />
          <PrivateRoute exact path="/close-till" component={CloseTill} />
          <PrivateRoute exact path="/close-till-all" component={CloseTillAll} />
          <PrivateRoute exact path="/table-management" component={TableManagement} errorBoundary="Table Management" />
          <PrivateRoute exact path="/call-center" component={CallCenter} errorBoundary="Call Center" />

          {/* Catch-all: redirect based on auth state */}
          <Route component={AuthRedirect} />
        </Switch>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
